import React, { useEffect, useMemo, useState } from "react";
import prescriptionsData from "../data/private/prescriptions.json";
import { logActivity } from "../utils/activity";
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";
import SectionCard from "../components/SectionCard";
import ClientInfoCard from "../components/ClientInfoCard";
import AnalysisSummaryCard from "../components/AnalysisSummaryCard";

const formatDate = () =>
  new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });

const hydrateTemplate = (template, tokens) =>
  template.replace(/\{(\w+)\}/g, (_, key) => tokens[key] || "");

export default function DashboardPrescriptions() {
  const clients = prescriptionsData.clients || [];
  const [selectedId, setSelectedId] = useState(() => {
    const stored = localStorage.getItem("myv_prescription_client");
    return stored || prescriptionsData.clients?.[0]?.id || "";
  });

  const activeClient = useMemo(
    () => clients.find((client) => client.id === selectedId),
    [clients, selectedId]
  );

  useEffect(() => {
    if (!activeClient) return;
    logActivity("prescription_viewed", {
      id: activeClient.id,
      name: activeClient.name,
    });
  }, [activeClient]);

  if (!clients.length) {
    return (
      <EmptyState
        title="No prescriptions yet"
        message="Prescriptions will appear once available."
        testId="dashboard-empty-prescriptions"
      />
    );
  }

  if (!activeClient) {
    return <ErrorState message="Unable to load prescriptions." />;
  }

  const whatsappNumber = activeClient.phone
    ? activeClient.phone.replace(/\D/g, "")
    : "";
  const shareText = hydrateTemplate(prescriptionsData.page.shareTemplate, {
    clientName: activeClient.name,
    property: activeClient.property,
  });
  const shareLink = `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodeURIComponent(
    shareText
  )}`;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">
          {prescriptionsData.page.title}
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          {prescriptionsData.page.subtitle}
        </p>
      </div>

      <div className="card p-4">
        <label className="text-xs text-slate-500">
          {prescriptionsData.page.selectLabel}
        </label>
        <select
          className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm md:max-w-sm"
          value={selectedId}
          onChange={(event) => {
            setSelectedId(event.target.value);
            localStorage.setItem("myv_prescription_client", event.target.value);
          }}
        >
          <option value="">{prescriptionsData.page.selectPlaceholder}</option>
          {clients.map((client) => (
            <option key={client.id} value={client.id}>
              {client.name}
            </option>
          ))}
        </select>
      </div>

      <div
        className="mx-auto w-full rounded-2xl bg-white p-6 shadow-lg print:rounded-none print:p-4 print:shadow-none"
        style={{ maxWidth: "210mm", minHeight: "297mm" }}
      >
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 pb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-amber-600">
              {prescriptionsData.page.logoLabel}
            </p>
            <p className="mt-2 text-lg font-semibold text-slate-900">
              {prescriptionsData.sections.footerLabel}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-400">
              {prescriptionsData.page.dateLabel}
            </p>
            <p className="text-sm font-semibold text-slate-700">
              {formatDate()}
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">
            {prescriptionsData.page.statusLabel}: {activeClient.status}
          </span>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">
            {prescriptionsData.page.revisionLabel}: {activeClient.revision}
          </span>
        </div>

        <div className="mt-6 grid gap-4">
          <div title={prescriptionsData.tooltips.clientDetails}>
            <ClientInfoCard
              labels={prescriptionsData.clientLabels}
              client={activeClient}
            />
          </div>

          <div title={prescriptionsData.tooltips.analysisSummary}>
            <AnalysisSummaryCard
              title={prescriptionsData.sections.analysisSummaryTitle}
              summary={activeClient.analysisSummary.summary}
              metrics={activeClient.analysisSummary.metrics}
              tooltip={activeClient.analysisSummary.headline}
            />
          </div>

          <div title={prescriptionsData.tooltips.recommendations}>
            <p className="text-sm font-semibold text-slate-900">
              {prescriptionsData.sections.recommendationsTitle}
            </p>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              {activeClient.recommendations.map((item) => (
                <SectionCard
                  key={item.title}
                  title={item.title}
                  description={item.description}
                  tag={prescriptionsData.sections.recommendationsTag}
                />
              ))}
            </div>
          </div>

          <div title={prescriptionsData.tooltips.remedies}>
            <p className="text-sm font-semibold text-slate-900">
              {prescriptionsData.sections.remediesTitle}
            </p>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              {activeClient.remedies.map((item) => (
                <SectionCard
                  key={item.title}
                  title={item.title}
                  description={item.description}
                  tag={prescriptionsData.sections.remediesTag}
                />
              ))}
            </div>
          </div>

          <div title={prescriptionsData.tooltips.productDetails}>
            <div className="card p-4">
              <p className="text-sm font-semibold text-slate-900">
                {prescriptionsData.sections.productTitle}
              </p>
              <p className="mt-2 text-sm text-slate-600">
                {prescriptionsData.sections.productLabel}:{" "}
                {activeClient.productUsed.plan}
              </p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
                {activeClient.productUsed.details.map((detail) => (
                  <span
                    key={detail}
                    className="rounded-full bg-slate-100 px-3 py-1"
                  >
                    {detail}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-[1fr_1fr]">
            <div className="card h-24 border border-dashed border-slate-200 p-4">
              <p className="text-xs text-slate-400">
                {prescriptionsData.sections.signatureLabel}
              </p>
            </div>
            <div className="flex items-end justify-end text-right text-xs text-slate-400">
              {prescriptionsData.sections.disclaimer}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4 text-xs text-slate-400">
          <span>{prescriptionsData.sections.footerLabel}</span>
          <span>{activeClient.name}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 print:hidden">
        <button
          className="btn btn-secondary"
          onClick={() =>
            logActivity("prescription_download", { id: activeClient.id })
          }
        >
          {prescriptionsData.page.downloadLabel}
        </button>
        <button
          className="btn btn-secondary"
          onClick={() => {
            logActivity("prescription_print", { id: activeClient.id });
            window.print();
          }}
        >
          {prescriptionsData.page.printLabel}
        </button>
        <a
          className={`btn btn-primary ${
            whatsappNumber ? "" : "pointer-events-none opacity-50"
          }`}
          href={whatsappNumber ? shareLink : "#"}
          target="_blank"
          rel="noreferrer"
          onClick={() =>
            logActivity("prescription_share", { id: activeClient.id })
          }
        >
          {prescriptionsData.page.shareLabel}
        </a>
      </div>

    </div>
  );
}
